/**
 * Stained glass coloring pages — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_coloring_themes`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const stainedGlassColoringPagesPack: ToilePack = {
    slug: 'stained-glass-coloring-pages',
    category: 'coloring',
    name: 'Stained glass coloring pages',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'rose', label: 'Rose window', subject: 'a round rose window with petal panes radiating from a central circle and a ring of small lancet arches'},
        {key: 'butterfly', label: 'Butterfly', subject: 'a butterfly with its wings divided into geometric panes, a segmented body and curled antennae'},
        {key: 'sunflower', label: 'Sunflower', subject: 'a tall sunflower in an arched window panel, its petals, seed head and leaves each a separate pane'},
    ],
    styles: [
        {key: 'gothic', label: 'Gothic window', style: 'thick black leading lines, large bold panes', palette: 'black ink only', framing: 'The design set in a pointed-arch window frame filling the page'},
        {key: 'mosaic', label: 'Mosaic panes', style: 'thick black leading lines, many small angular geometric panes in a tiffany-lamp mosaic', palette: 'black ink only', framing: 'The design filling the rectangle edge to edge inside a thick black border'},
        {key: 'nouveau', label: 'Art nouveau', style: 'thick black leading lines in flowing whiplash curves, with organic curved panes', palette: 'black ink only', framing: 'The design in an ornate curved frame with a white margin'},
    ],
    directions: ['dragonfly', 'owl', 'peacock'],
    directionHint: 'try: dragonfly',
    seo: {
        title: 'Stained glass coloring pages: rose, butterfly, sunflower',
        metaDescription: 'Nine stained glass coloring pages: a rose window, a butterfly and a sunflower in thick black leading, in three styles.',
        h1: 'Stained glass coloring pages: rose window, butterfly, sunflower',
        tagline: 'Bold fills divided into panes by thick black leading, for pencils, markers or tracing paper.',
        intro: 'Stained glass coloring pages for adults and older children who like bold, satisfying fills. The nine pages show a rose window, a butterfly and a sunflower, each divided into panes by thick black leading. The styles are Gothic window, mosaic and art nouveau. Some people color these on tracing paper to make window art. Type a different subject, such as an owl or a peacock, and the same style carries it.',
        useCases: [
            'a window art project on tracing paper',
            'a bold page for an adult colorer',
            'a classroom study of stained glass windows',
            'a page for a sun-catcher craft',
        ],
        faq: [
            {q: 'What is the difference between the styles?', a: 'The Gothic window style has thick leading and large bold panes. The mosaic style has many small angular panes, and art nouveau has flowing curved leading with organic panes.'},
            {q: 'Can I use these for window art?', a: 'Yes. Some people color these on tracing paper to make window art.'},
            {q: 'Can I ask for a different subject?', a: 'Yes. A subject you type, such as a dragonfly, an owl or a peacock, is a request; the page keeps the same style but may not match it exactly.'},
            {q: 'Are the pages in color?', a: 'No. Each page is a black line drawing on white paper, ready to print and fill with color.'},
        ],
    },
}
